import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Download, Map as MapIcon, RefreshCw } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  WORK_AREA_STATUS_LABELS,
  collectWorkAreaConsolidation,
  isIncluded,
  type ConsolidationResult,
  type Resolutions,
  type WorkAreaGroup,
} from "@/lib/basProcess/consolidation";
import { generateConsolidatedWorkAreaWord } from "@/lib/documentGenerator/consolidatedWordGenerator";

interface Props {
  projectId: string;
}

export const ConsolidatedWorkAreaSurvey: React.FC<Props> = ({ projectId }) => {
  const [result, setResult] = useState<ConsolidationResult<WorkAreaGroup> | null>(null);
  const [resolutions, setResolutions] = useState<Resolutions>({});
  const [recordId, setRecordId] = useState<string | null>(null);
  const [title, setTitle] = useState("Samlad arbetsområdesutredning");
  const [notes, setNotes] = useState("");
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [onlyConflicts, setOnlyConflicts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [data, { data: rec }, { data: proj }] = await Promise.all([
      collectWorkAreaConsolidation(projectId),
      supabase
        .from("consolidated_documents")
        .select("*")
        .eq("project_id", projectId)
        .eq("doc_type", "work_area")
        .maybeSingle(),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setResult(data);
    setProjectName((proj as { name?: string } | null)?.name ?? "");
    const row = rec as unknown as Record<string, any> | null;
    setRecordId(row?.id ?? null);
    setResolutions((row?.resolutions as Resolutions) ?? {});
    setTitle(row?.title || "Samlad arbetsområdesutredning");
    setNotes(row?.notes ?? "");
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    const payload = {
      project_id: projectId,
      doc_type: "work_area",
      title,
      notes,
      resolutions: resolutions as unknown as Record<string, unknown>,
      sources: (result?.sources ?? []) as unknown as Record<string, unknown>[],
      updated_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    };
    const { data, error } = recordId
      ? await supabase.from("consolidated_documents").update(payload as any).eq("id", recordId).select("id").single()
      : await supabase.from("consolidated_documents").insert(payload as any).select("id").single();
    setBusy(false);
    if (error) return toast.error("Kunde inte spara samordningen");
    setRecordId((data as { id: string }).id);
    toast.success("Samordningen sparad");
  };

  const patch = (key: string, value: Partial<Resolutions[string]>) =>
    setResolutions((prev) => ({ ...prev, [key]: { ...prev[key], ...value } }));

  const sourceLabel = useMemo(
    () => new Map((result?.sources ?? []).map((s) => [s.id, s.label])),
    [result]
  );

  const visibleGroups = useMemo(() => {
    const groups = result?.groups ?? [];
    return onlyConflicts ? groups.filter((g) => g.conflict) : groups;
  }, [result, onlyConflicts]);

  const includedCount = (result?.groups ?? []).filter((g) => isIncluded(resolutions, g.key)).length;
  const openConflicts = (result?.groups ?? []).filter(
    (g) => g.conflict && isIncluded(resolutions, g.key) && !resolutions[g.key]?.decision
  ).length;

  const exportWord = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await generateConsolidatedWorkAreaWord({
        projectName,
        title,
        sources: result.sources,
        groups: result.groups,
        resolutions,
        notes,
      });
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="10b"
        title="Samlad arbetsområdesutredning"
        description="Jämför alla arbetsområden sida vid sida. Kontrollpunkter som bedömts olika mellan områden lyfts fram så att Bas-P kan ta ett gemensamt beslut."
        legalRef="AFS 2023:3 kap. 4"
        icon={MapIcon}
        projectId={projectId}
        meta={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{result?.sources.length ?? 0} områden</Badge>
            <Badge variant="secondary">{includedCount} kontrollpunkter</Badge>
            {openConflicts > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> {openConflicts} obeslutade skillnader
              </Badge>
            )}
          </div>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
              <RefreshCw className="mr-1 h-4 w-4" /> Uppdatera underlag
            </Button>
            <Button variant="outline" size="sm" onClick={save} disabled={busy}>
              Spara
            </Button>
            <Button size="sm" onClick={exportWord} disabled={busy || !result}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Dokumentets namn</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Kommentar från Bas-P</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={onlyConflicts}
              onCheckedChange={(v) => setOnlyConflicts(v === true)}
            />
            Visa bara punkter där områdena bedömts olika
          </label>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Läser underlag …</p>
      ) : visibleGroups.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Det finns inga arbetsområdesutredningar att samordna ännu.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleGroups.map((g) => {
            const r = resolutions[g.key] ?? {};
            return (
              <Card key={g.key} className={g.conflict ? "border-destructive/40" : undefined}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{g.controlPoint}</p>
                      <p className="text-xs text-muted-foreground">
                        {g.category}
                        {g.legalReference ? ` · ${g.legalReference}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {g.conflict && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" /> Olika bedömning
                        </Badge>
                      )}
                      <label className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={r.include !== false}
                          onCheckedChange={(v) => patch(g.key, { include: v === true })}
                        />
                        Ta med
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {g.entries.map((e, i) => (
                      <Badge key={i} variant="outline" className="font-normal">
                        {sourceLabel.get(e.sourceId) ?? "Område"}:{" "}
                        {WORK_AREA_STATUS_LABELS[e.status ?? ""] ?? "–"}
                      </Badge>
                    ))}
                  </div>

                  {g.entries.some((e) => e.comment) && (
                    <div className="space-y-1 rounded-lg border border-border/60 bg-muted/30 p-2 text-sm">
                      {g.entries
                        .filter((e) => e.comment)
                        .map((e, i) => (
                          <p key={i}>
                            <span className="text-xs text-muted-foreground">
                              {sourceLabel.get(e.sourceId) ?? "Område"}:{" "}
                            </span>
                            {e.comment}
                          </p>
                        ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">Bas-P beslut och motivering</Label>
                    <Input
                      value={r.decision ?? ""}
                      onChange={(e) => patch(g.key, { decision: e.target.value })}
                      placeholder="T.ex. Punkten hanteras gemensamt i AMP."
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConsolidatedWorkAreaSurvey;
